/**
 * Recorded one-shots.
 * Trick clicks ship in the repo and are trimmed at playback.
 * Tile cues are the v3 studio files in `public/audio/`. A missing decode
 * keeps the synthesized chime.
 */
import { TRICK_UI_GAIN as TRIMMED_TRICK_UI_GAIN } from './levels';

export const TRICK_UI = ['select', 'deselect'] as const;
export type TrickUi = (typeof TRICK_UI)[number];

/**
 * Both clicks use this gain. Deselect is already mixed a little softer in the
 * file, so the same trim keeps that relationship. The trim is -5 dB.
 */
export const TRICK_UI_GAIN = TRIMMED_TRICK_UI_GAIN;

export function trickUiGain(which: TrickUi): number {
  return which === 'select' || which === 'deselect' ? TRICK_UI_GAIN : TRICK_UI_GAIN;
}

const TRICK_UI_FILE: Record<TrickUi, string> = {
  select: 'ui-trick-select',
  deselect: 'ui-trick-deselect',
};

export function trickUiFile(which: TrickUi): string {
  return TRICK_UI_FILE[which];
}

/**
 * Names `play()` still accepts. swap, match, cascade, reject, and special
 * resolve to studio files. Win, ui, and the signature power stay
 * synthesized. The turn clock uses timer-tick-5 … timer-zero, not `tick`.
 */
export const BOARD_CUES = ['swap', 'match', 'cascade', 'reject', 'tick', 'win'] as const;
export type BoardCue = (typeof BOARD_CUES)[number];

/** Studio tile files are already at the sheet, so the bus stays at unity. */
export const BOARD_SFX_GAIN = 1;

/** Match of 3 cycles these. A match of 4 or more uses the bigger pop. */
export function matchPopFile(size: number, spin: number): string {
  if (size >= 4) return 'match4-pop';
  const step = ((Math.trunc(spin) % 3) + 3) % 3;
  return `match-pop-${step + 1}`;
}

/** Cascade index is 1-based. Later waves climb, and step 5 holds. */
export function cascadeFile(index: number): string {
  const step = Math.min(5, Math.max(1, Math.trunc(index) || 1));
  return `cascade-${step}`;
}

/** File for a legacy cue name. Null means the synth still plays. */
export function recordedBoardFile(name: string, matchSpin = 0): string | null {
  if (name === 'swap') return 'swap';
  if (name === 'reject') return 'illegal-bump';
  if (name === 'match') return matchPopFile(3, matchSpin);
  if (name === 'cascade') return cascadeFile(1);
  if (name === 'special') return 'special-create';
  return null;
}

export function boardCueFile(cue: BoardCue): string {
  return recordedBoardFile(cue) ?? `sfx-${cue}`;
}

export function isBoardCue(name: string): name is BoardCue {
  return (BOARD_CUES as readonly string[]).includes(name);
}

/** Ogg first. M4a is the fallback, same as the stingers. */
export function clipSources(file: string, support: { ogg: boolean; m4a: boolean }): string[] {
  const ogg = `/audio/${file}.ogg`;
  const m4a = `/audio/${file}.m4a`;
  if (support.ogg && support.m4a) return [ogg, m4a];
  if (support.ogg) return [ogg];
  if (support.m4a) return [m4a];
  return [ogg, m4a];
}

/** A decoded buffer plays. A miss, or a file that is not in the folder yet, stays on the synth. */
export function cuePlayback(buffer: unknown): 'recorded' | 'synth' {
  return buffer ? 'recorded' : 'synth';
}

/** Studio one-shots preloaded once the title is up. */
export const STUDIO_CLIPS = [
  'swap',
  'illegal-bump',
  'tile-land',
  'match-pop-1',
  'match-pop-2',
  'match-pop-3',
  'match4-pop',
  'cascade-1',
  'cascade-2',
  'cascade-3',
  'cascade-4',
  'cascade-5',
  'special-create',
  'trick-cast-shared',
  'accent-universal',
  'accent-stealth',
  'accent-infiltration',
  'accent-guardian',
  'accent-tidecaller',
  'accent-grove',
] as const;
